import { Tab, Tabs, Tooltip, TooltipProps } from '@mui/material';
import { ComponentType } from 'react';
import { useIntl } from 'react-intl';
import { PostProcessingTab } from '~/state/atoms/toolsAtoms';
import { POST_PROCESSING_TABS } from './postProcessingTabs';
import CropTab from './CropTab';
import ThinTab from './ThinTab';
import FillTab from './FillTab';
import PartsTab from './PartsTab';
import SmoothTab from './SmoothTab';

/**
 * The tab strip's tooltip wrapper. {@link Tabs} identifies each tab by the
 * `value` prop of its DIRECT child — here the Tooltip — and clones the
 * selection props onto it; the Tooltip forwards every prop it does not
 * consume down to the Tab it wraps. Its public type does not declare
 * `value` (it declares no forwarded props at all), so the one prop Tabs
 * reads back off the element is declared here rather than cast away at the
 * call site. Copied from BodyContourOperationContent's PresetTabTooltip.
 */
const PostProcessingTabTooltip = Tooltip as ComponentType<
    TooltipProps & { value: PostProcessingTab }
>;

interface PostProcessingOperationContentProps {
    tab: PostProcessingTab;
    onTabChange: (tab: PostProcessingTab) => void;
    /** Reports that a tab is mid-write, so the panel holds its close. */
    onPreventCloseChange: (prevent: boolean) => void;
    /**
     * A commit is in flight. The strip reads as unavailable for as long as
     * it lasts: leaving the tab unmounts the tab that is writing, and its
     * cleanup would release the hold keeping the panel open.
     */
    tabsDisabled?: boolean;
}

/**
 * Post Processing panel — a single toolbar entry point opening a draggable
 * panel with five icon tabs (Thin, Fill holes, Smooth, Parts, Crop).
 */
export default function PostProcessingOperationContent({
    tab,
    onTabChange,
    onPreventCloseChange,
    tabsDisabled = false,
}: PostProcessingOperationContentProps) {
    const intl = useIntl();

    return (
        <>
            <Tabs
                value={tab}
                onChange={(_, value) => onTabChange(value as PostProcessingTab)}
                variant="fullWidth"
                indicatorColor="primary"
                textColor="inherit"
                sx={{
                    minHeight: 32,
                    '& .MuiTab-root': {
                        minHeight: 32,
                        minWidth: 0,
                        py: 0.5,
                    },
                }}
            >
                {POST_PROCESSING_TABS.map(({ value, labelId, Icon }) => {
                    const label = intl.formatMessage({ id: labelId });
                    return (
                        // value on the Tooltip as well as the Tab: Tabs reads
                        // `child.props.value` off its DIRECT child to map the
                        // selection, and the Tooltip is that child. It
                        // forwards everything Tabs clones onto it (selected,
                        // indicator, onChange) down to the Tab, whose own
                        // props win.
                        <PostProcessingTabTooltip
                            key={value}
                            value={value}
                            title={label}
                            placement="bottom"
                        >
                            <Tab
                                value={value}
                                icon={<Icon />}
                                aria-label={label}
                                disabled={tabsDisabled}
                            />
                        </PostProcessingTabTooltip>
                    );
                })}
            </Tabs>
            {/* No OperationContentShell here, deliberately: gating is
                already airtight without it — the entry ToolbarButton's
                disabled predicate keeps the panel from opening without a
                selected ROI, and PostProcessingSection's auto-close effect
                closes it if the selection is lost while it's open, exactly
                as FillCavitiesPanel worked before this move (it never used
                the shell either). The shell's render props (structures,
                onInputROIChange, ...) aren't needed by any tab planned for
                this panel, so don't reach for it here to fill the empty
                tabs below — that would be new gating, not a move. */}
            {tab === 'thin' && (
                <ThinTab onPreventCloseChange={onPreventCloseChange} />
            )}
            {tab === 'fill' && <FillTab />}
            {tab === 'smooth' && <SmoothTab />}
            {tab === 'parts' && <PartsTab />}
            {tab === 'crop' && (
                <CropTab onPreventCloseChange={onPreventCloseChange} />
            )}
        </>
    );
}
